import { useSelector } from 'react-redux';
import { AccessLevel, NotificationType, ProductCode } from '@dev/ee-api/ee-api-models';
import { identity, isEmpty } from 'lodash-es';

import dayjs from '@api/services/tools/dayjs';
import { getNotificationStore } from '@api/store/notifications/actions';
import { getAccountStore } from '@api/store/account/actions';
import { getProductStore } from '@api/store/products/actions';
import { convertToLocalTime } from '@dev/ee-tools';

export interface DiscountInterface {
    discountEnabled: boolean;
    discount: number;
    discountDuration: number;
    discountExpireDay: string;
}

const DISCOUNT_BANNER_NAME = 'Promotion discount';

export const useDiscount = (): DiscountInterface => {
    const { notificationList } = useSelector(getNotificationStore);

    const { GeneralInfo, AdvancedOptions } = useSelector(getAccountStore) || {};
    const { dashboards } = useSelector(getProductStore);

    const discountBanner = notificationList?.find((n) => n.Type === NotificationType.Banner && n.Name === DISCOUNT_BANNER_NAME);
    const { Content, ProductCodes } = discountBanner || {};

    const accountConditionForHiddenDiscount = {
        users: GeneralInfo?.IsUser && !GeneralInfo?.AccountAccessLevel?.includes(AccessLevel.ViewBilling),
        subAccounts: GeneralInfo?.IsSub,
        customBranding: AdvancedOptions?.IsOwnedByReseller,
        accountHasDiscountAlready: dashboards?.some(
            ({ ActivePlanDetails }) =>
                ActivePlanDetails?.Discounts?.DiscountExpiry && !(ActivePlanDetails?.IsTrial || ActivePlanDetails.IsFree)
        ),
        discountHasNoValue: !Content?.DiscountPercent,
    };

    const backendConditionsToHideDiscount = {
        accHasDiscountSet: !discountBanner && !ProductCodes?.includes[ProductCode?.EmailApi],
    };

    const hideDiscount = Object.values({ ...accountConditionForHiddenDiscount, ...backendConditionsToHideDiscount })?.some(identity);

    const promoExpiryDateAsUTC = dayjs(Content?.PromoExpiry).utc(true).toString();

    return {
        discountEnabled: !hideDiscount && !isEmpty(dashboards),
        discount: +Content?.DiscountPercent * 100,
        discountDuration: +Content?.DiscountDurationMonths,
        discountExpireDay: convertToLocalTime(promoExpiryDateAsUTC),
    };
};
